import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Draw border on all pages
const drawPageBorder = (doc) => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.rect(2, 2, 206, 288);
};

// Format currency
const formatCurrency = (num) => {
    if (typeof num !== 'number') return num;
    return `${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

// Export to PDF
export const exportToPDF = (data, company, setLoading, navigate) => {
    try {
        setLoading(true);
        const doc = new jsPDF('p', 'mm', 'a4');
        let y = 12;

        // --- HEADER ---
        doc.setFillColor(41, 128, 185);
        doc.rect(2, 2, 206, 36, 'F');
        doc.setFontSize(20);
        doc.setTextColor(255, 255, 255);
        doc.text(company?.[0]?.name || '', 105, 10, { align: 'center', baseline: 'middle' });

        doc.setFontSize(9);
        let address = (company?.[0]?.address || '')
            .replace(/<[^>]+>/g, '')
            .replace(/[^\x20-\x7E\n,.-]/g, '');
        const addressLines = address.split(/\\n|\n/).map(line => line.trim()).filter(Boolean);
        let addrY = 20;
        addressLines.forEach(line => {
            doc.text(line, 105, addrY, { align: 'center' });
            addrY += 4.5;
        });

        doc.setTextColor(0, 0, 0);
        y = 40;
        drawPageBorder(doc);

        // --- CUSTOMER INFO ---
        doc.setDrawColor(41, 128, 185);
        doc.setLineWidth(0.3);
        doc.roundedRect(10, y, 190, 44, 3, 3);
        doc.setFontSize(10);
        doc.text(`To: ${data?.customer?.name || ''}`, 14, y + 7);
        doc.text(`Quotation No.: ${data?.quotationId || ''}`, 167, y + 7);

        let custAddr = (data?.customer?.address || '')
            .replace(/<[^>]+>/g, '')
            .replace(/\\n|\n/g, ' ')
            .replace(/[^\x20-\x7E,.-]/g, '');
        doc.text(custAddr, 14, y + 13);
        doc.text(`Date: ${new Date().toLocaleDateString()}`, 167, y + 13);
        if (data?.customer?.gstno) doc.text(`GST No: ${data.customer.gstno}`, 14, y + 27);
        if (data?.customer?.contactPerson) doc.text(`Contact: ${data.customer.contactPerson}`, 14, y + 33);
        if (data?.customer?.mobileNo) doc.text(`Mobile: ${data.customer.mobileNo}`, 14, y + 39);
        y += 55;

        // --- SUMMARY TITLE ---
        doc.setFontSize(15);
        doc.setTextColor(41, 128, 185);
        doc.setFont(undefined, 'bold');
        doc.text('QUOTATION SUMMARY', 12, y);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
        y += 5;

        // --- SUMMARY TABLE ---
        const summaryRows = [];
        let grandTotalWithoutGST = 0, grandGST = 0, grandTotalWithGST = 0;
        data?.data?.forEach((section, idx) => {
            let sectionTotalWithoutGST = 0, sectionGST = 0, sectionTotalWithGST = 0;
            section.items.forEach(item => {
                const qty = Number(item.quantity) || 1;
                const rate = Number(item.rate) || 0;
                const totalWithoutGST = qty * rate;
                const gstPercent = Number(item.gst) || 0;
                const gstAmount = totalWithoutGST * gstPercent / 100;
                const totalWithGST = totalWithoutGST + gstAmount;
                sectionTotalWithoutGST += totalWithoutGST;
                sectionGST += gstAmount;
                sectionTotalWithGST += totalWithGST;
            });
            summaryRows.push([
                idx + 1,
                section.location || '',
                section.quantity || 1,
                formatCurrency(sectionTotalWithoutGST / (section.quantity || 1)),
                formatCurrency(sectionTotalWithoutGST),
                formatCurrency(sectionGST),
                formatCurrency(sectionTotalWithGST)
            ]);
            grandTotalWithoutGST += sectionTotalWithoutGST;
            grandGST += sectionGST;
            grandTotalWithGST += sectionTotalWithGST;
        });

        // Add Grand Total row with styling
        summaryRows.push([
            {
                content: 'GRAND TOTAL',
                colSpan: 4,
                styles: { fontStyle: 'bold', fillColor: [240, 240, 240], halign: 'right' }
            },
            { content: formatCurrency(grandTotalWithoutGST), styles: { fontStyle: 'bold' } },
            { content: formatCurrency(grandGST), styles: { fontStyle: 'bold' } },
            { content: formatCurrency(grandTotalWithGST), styles: { fontStyle: 'bold' } }
        ]);

        autoTable(doc, {
            startY: y,
            head: [['S.No', 'Section', 'Qty', 'Rate', 'Total Without GST', 'GST Amount', 'Total With GST']],
            body: summaryRows,
            styles: {
                fontSize: 8,
                cellPadding: 2,
                halign: 'center',
                valign: 'middle',
                lineWidth: 0.2,
                lineColor: [0, 0, 0]
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: 'bold',
                lineWidth: 0.2,
                lineColor: [0, 0, 0]
            },
            columnStyles: {
                0: { cellWidth: 8 },
                1: { cellWidth: 32 },
                2: { cellWidth: 10 },
                3: { cellWidth: 35 },
                4: { cellWidth: 35 },
                5: { cellWidth: 30 },
                6: { cellWidth: 37 }
            },
            margin: { left: 10, right: 5 }
        });


        // --- SECTION SHEETS ---
        doc.addPage();
        drawPageBorder(doc);
        let sy = 18;

        data?.data?.forEach((section, idx) => {
            if (idx > 0 && doc.lastAutoTable?.finalY) {
                sy = doc.lastAutoTable.finalY + 10;
            }

            doc.setFontSize(14);
            doc.setTextColor(41, 128, 185);
            doc.setFont('helvetica', 'bold');
            doc.text((section.location || `Section ${idx + 1}`).toUpperCase(), 12, sy);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 0);
            sy += 6;

            const sectionRows = [];
            let serial = 1;
            let totalWithoutGST = 0, totalGST = 0, totalWithGST = 0;

            const itemsByCategory = section.items.reduce((acc, item) => {
                const category = item.category || 'Uncategorized';
                if (!acc[category]) acc[category] = [];
                acc[category].push(item);
                return acc;
            }, {});

            Object.entries(itemsByCategory).forEach(([category, items]) => {
                sectionRows.push([{
                    content: category,
                    colSpan: 10,
                    styles: {
                        fontStyle: 'bold',
                        fillColor: [220, 230, 241],
                        halign: 'left'
                    }
                }]);
                items.forEach(item => {
                    const qty = Number(item.quantity) || 1;
                    const rate = Number(item.rate) || 0;
                    const total = qty * rate;
                    const gstPercent = Number(item.gst) || 0;
                    const gstAmt = total * gstPercent / 100;
                    const withGst = total + gstAmt;
                    totalWithoutGST += total;
                    totalGST += gstAmt;
                    totalWithGST += withGst;

                    const desc = (item.shortDescription || '').replace(/<[^>]+>/g, '').replace(/\n/g, ' ');
                    sectionRows.push([
                        serial++,
                        item.name || '',
                        desc,
                        qty,
                        formatCurrency(rate),
                        formatCurrency(total),
                        `${gstPercent}%`,
                        formatCurrency(gstAmt),
                        formatCurrency(withGst),
                        item.remarks || ''
                    ]);
                });
            });

            // Add section total row
            sectionRows.push([
    {
        content: 'Section Total',
        colSpan: 5,
        styles: { fontStyle: 'bold', fillColor: [240, 240, 240], halign: 'right' }
    },
    { content: formatCurrency(totalWithoutGST), styles: { fontStyle: 'bold' } },
    '',
    { content: formatCurrency(totalGST), styles: { fontStyle: 'bold' } },
    { content: formatCurrency(totalWithGST), styles: { fontStyle: 'bold' } },
    ''
]);


            autoTable(doc, {
                startY: sy,
                head: [
                    ['S.No', 'Item Name', 'Description', 'Qty', 'Rate', 'Total Without GST', 'GST %', 'GST Amt', 'Total With GST', 'Remarks']
                ],
                body: sectionRows,
                styles: {
                    fontSize: 6,
                    cellPadding: 1.5,
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.2,
                    lineColor: [0, 0, 0]
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: 'bold',
                    lineWidth: 0.2,
                    lineColor: [0, 0, 0]
                },
                columnStyles: {
                    0: { cellWidth: 5 },
                    1: { cellWidth: 24 },
                    2: { cellWidth: 30 },
                    3: { cellWidth: 7 },
                    4: { cellWidth: 26 },
                    5: { cellWidth: 30 },
                    6: { cellWidth: 10 },
                    7: { cellWidth: 26 },
                    8: { cellWidth: 30 },
                    9: { cellWidth: 15 }
                },
                margin: { left: 3.5, right: 2 }
            });
        });

        // --- TERMS & CONDITIONS ---
        doc.addPage();
        drawPageBorder(doc);
        let ty = 28;
        doc.setFontSize(14);
        doc.setTextColor(41, 128, 185);
        doc.text('Terms and Conditions', 12, ty - 10);

        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        const maxWidth = 180;
        const lineHeight = 6;
        let termsText = (data?.terms || '').replace(/<[^>]+>/g, '');
        termsText.split('\n').forEach((line) => {
            const wrappedLines = doc.splitTextToSize(line, maxWidth);
            wrappedLines.forEach((textLine) => {
                doc.text(textLine, 12, ty);
                ty += lineHeight;
            });
        });

        // --- Sales Person Info ---
        let salesY = ty + 6;
        doc.setFontSize(11);
        doc.setTextColor(41, 128, 185);
        doc.text('With Regards,', 12, salesY);
        doc.setTextColor(0, 0, 0);
        salesY += 7;
        doc.setFontSize(10);
        if (data?.salesData?.name) {
            doc.text(data.salesData.name, 12, salesY);
            salesY += 6;
        }
        if (data?.salesData?.phoneNo) {
            doc.text(data.salesData.phoneNo, 12, salesY);
            salesY += 6;
        }
        if (data?.salesData?.email) {
            doc.text(data.salesData.email, 12, salesY);
            salesY += 6;
        }

        doc.save(`Invoice_Export_${new Date().toISOString().slice(0, 10)}.pdf`);
        setLoading(false);
    } catch (error) {
        setLoading(false);
        alert('An error occurred while generating the PDF. Please contact the developer.');
        navigate('/');
    }
};
